'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ironhack/design-system2/components/button';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { Text } from '@ironhack/design-system2/components/text';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import { useRouter } from 'next/navigation';
import { type ReactElement, useState } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';
import { FormProvider, useForm } from 'react-hook-form';
import { useSessionStorage } from 'react-use';

import { getButtonIconProps } from '@/app/components/client/button';
import { CheckboxField } from '@/app/components/client/checkbox-field';
import { StructuredContent } from '@/app/components/client/structured-content';
import { TextField } from '@/app/components/client/text-field';
import { type QuizSummaryData } from '@/app/components/server/quiz-match-header';
import { getPageUrl } from '@/app/urls/url-mapping';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { useFormRequest } from '@/hooks/use-form-request';
import {
  type DatoBackgroundColor,
  type DatoButtonBlock,
} from '@/lib/datocms/types';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import { type ValidationMessageSet } from '@/lib/utils/form-validations';
import { getNurturingNewsletterSchema } from '@/lib/utils/form-validations/forms/nurturing-form';
import { type EmailInput } from '@/lib/utils/form-validations/schemas/email-schema';
import { type SelectedCourseAnswer } from '../../icon-question';
import { type ImageAnswer } from '../../image-question/image-answer';
import { calculateWinningTrack } from './calculate-winning-track';
import { getSalesforceQuestionStructure } from './get-salesforce-question-structure';

type FormData = {
  cToken: string;
  newsletter: boolean;
} & EmailInput;

export type CallToActionForm = {
  backgroundColor: DatoBackgroundColor;
  description: StructuredTextGraphQlResponse;
  errorMessage: string;
  emailPlaceholder: string;
  emailValidationMessages: ValidationMessageSet;
  newsletterEnabled: boolean;
  newsletterLabel: string;
  submitButton: DatoButtonBlock;
  successMessage: string;
  title: string;
};

type Props = {
  answers: Array<SelectedCourseAnswer | ImageAnswer>;
  data: CallToActionForm;
  sessionProfile: QuizSummaryData;
} & FlexProps;

export const CallToActionForm = (props: Props): ReactElement => {
  const {
    answers,
    data: {
      backgroundColor: { backgroundColor: formBackground },
      description,
      emailPlaceholder,
      emailValidationMessages,
      errorMessage,
      newsletterEnabled,
      newsletterLabel,
      submitButton: {
        text: buttonText,
        icon: buttonIcon,
        variant: buttonVariant,
      },
      successMessage,
      title,
    },
    sessionProfile,
    ...flexProps
  } = props;

  const { params } = usePageContext();
  const { language } = params;

  const [, setQuizAnswerProfile] = useSessionStorage('quizAnswerProfile');
  const [, setFormEmail] = useSessionStorage('formEmail');

  const router = useRouter();
  const formRequest = useFormRequest();
  const [formFeedback, setFormFeedback] = useState<{
    status: 'error' | 'success';
    message: string;
  } | null>();
  const isRedirecting = formFeedback?.status === 'success';

  const formSchema = getNurturingNewsletterSchema({
    emailValidationMessages,
    submitError: errorMessage,
  });
  const formMethods = useForm<FormData>({
    defaultValues: { newsletter: false },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
  } = formMethods;
  const setCaptchaToken = (token: string) => setValue('cToken', token);

  const onSubmit = async (formData: FormData): Promise<void> => {
    if (isRedirecting) return;

    const { newsletter, ...data } = formData;
    const winningTrack = calculateWinningTrack(answers);
    const salesforceQuestionStructure = getSalesforceQuestionStructure(answers);
    try {
      await formRequest({
        data: {
          ...data,
          ...salesforceQuestionStructure,
          bootcamp: `${winningTrack.toUpperCase()}FT`,
          emailOptIn: newsletter,
          language,
          source: 'Website_Homepage_Quiz_v1',
        },
        formType: 'nurturing-quiz',
      });

      const matchUrl = getPageUrl(`quizMatch/${winningTrack}`, params);
      if (!matchUrl) {
        throw new Error('Invalid match URL');
      }

      setFormFeedback({ status: 'success', message: successMessage });
      setQuizAnswerProfile(sessionProfile);
      setFormEmail(formData.email);
      router.push(matchUrl);
    } catch {
      setFormFeedback({ status: 'error', message: errorMessage });
    }
  };

  const buttonIconProps = getButtonIconProps(buttonIcon?.[0]);

  return (
    <FormProvider {...formMethods}>
      <Flex
        as="form"
        background={`background.${formBackground}`}
        borderRadius="lg"
        direction="column"
        gap={3}
        maxWidth="440px"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        padding={2}
        {...flexProps}
      >
        <Flex direction="column" gap={2}>
          <Text as="h2" textStyle="2xl">
            {title}
          </Text>

          {!isEmptyDocument(description) && (
            <StructuredContent data={description} textStyle="m" />
          )}
        </Flex>

        <Flex direction="column" gap={2}>
          <TextField
            error={errors.email?.message}
            isRequired
            name="email"
            placeholder={emailPlaceholder}
            type="email"
          />

          <TurnstileFormToken
            includeMargin={false}
            language={language}
            onVerify={setCaptchaToken}
          />

          {newsletterEnabled && (
            <CheckboxField
              error={errors.newsletter?.message}
              isRequired
              label={newsletterLabel}
              matchCaptcha
              name="newsletter"
            />
          )}

          <Button
            isLoading={isSubmitting || isRedirecting}
            type="submit"
            variant={buttonVariant}
            width="full"
            {...buttonIconProps}
          >
            {buttonText}
          </Button>
          {formFeedback?.status && <FormFeedback {...formFeedback} />}
        </Flex>
      </Flex>
    </FormProvider>
  );
};
